import type { ComponentType, ReactNode } from 'react';
import { CheckCircle, Info, WarningCircle } from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { TONE_TEXT, type Tone } from '../tone';
import { Icon } from '../Icon';

/** The shared status vocabulary (ui/tone.ts); attention is for errors and warnings. */
export type InlineAlertTone = Tone;

const GLYPH: Record<Tone, ComponentType<IconProps>> = {
  neutral: Info,
  info: Info,
  positive: CheckCircle,
  attention: WarningCircle,
};
const BOX: Record<Tone, string> = {
  neutral: 'bg-jc-elevated',
  info: 'bg-jc-forest-soft',
  positive: 'bg-jc-forest-soft',
  attention: 'border-l-4 border-jc-ink bg-jc-elevated',
};

export type InlineAlertProps = {
  tone?: InlineAlertTone;
  title: ReactNode;
  children?: ReactNode;
  /** One action, e.g. a tertiary Button ("Try again"). */
  action?: ReactNode;
  /**
   * Defaults to alert for `attention` (announced) and note otherwise. Static content that only
   * warns, such as a "Watch out" box in an article, passes `note` so it is not announced on load.
   */
  role?: 'alert' | 'note';
  className?: string;
};

/**
 * A message that stays in the page beside what it is about. Errors are announced (role="alert")
 * and say how to fix the problem; everything else is a note. Icon plus words, never colour alone.
 */
export function InlineAlert({ tone = 'info', title, children, action, role, className }: InlineAlertProps) {
  return (
    <div
      role={role ?? (tone === 'attention' ? 'alert' : 'note')}
      className={cx('flex gap-3 rounded-jc-info p-4 text-jc-ink', BOX[tone], className)}
    >
      <span className={cx('mt-0.5 shrink-0', TONE_TEXT[tone])}>
        <Icon icon={GLYPH[tone]} size="md" weight="fill" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-sm font-semibold">{title}</p>
        {children ? <div className="text-sm text-jc-ink-secondary">{children}</div> : null}
        {action ? <div className="mt-1">{action}</div> : null}
      </div>
    </div>
  );
}
